"use client";
import { Api } from "@/Apis";
import { LandingConversationTarget } from "@/Areas/Landing/Components/LandingConversationProvider";
import {
  LandingDoctorGetDetailExtraOutputApiModel,
  LandingDoctorGetDetailOutputApiModel,
  LandingSiteTemplateGetDataOutputApiModel,
} from "@/Apis/ClinicApi";
import useLandingAppointmentModal from "@/Areas/Landing/Pages/LandingAppointment/useLandingAppointmentModal";
import { LandingCommentListPage } from "@/Areas/Landing/Pages/LandingComment";
import {
  FavoriteToggle,
  Format,
  Holidays,
  Html,
  If,
  SmartImage,
  SmartLink,
} from "@/Components";
import { PhoneNumberTag } from "@/Components/Tags";
import { countryConfig } from "@/config/country";
import { Constants, Helper } from "@/Helpers";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { resolveLocalizedText } from "@/Helpers/language";
import { useApp, useAppSelector, useCenterOptionEnums, useT } from "@/Hooks";
import { Breadcrumb, RatingStars } from "@/Templates/Doccure/Components";
import { Blogs, Commodities, Faq, Gallery } from "@/Templates/Doccure/Sections";
import { defaultBlogsSetting } from "@/Templates/Doccure/Sections/Blogs";
import { defaultFaqSetting } from "@/Templates/Doccure/Sections/Faq";
import { Icon } from "@iconify/react";
import { Alert, Card, Flex, Space, Typography } from "antd";
import _ from "lodash";
import { FC, useEffect, useMemo, useState } from "react";
import { FiMapPin, FiPhone, FiShare2 } from "react-icons/fi";
import { MdVerified } from "react-icons/md";

const { Text, Title, Paragraph } = Typography;

interface LandingDoctorProfilePageProps {
  data: LandingDoctorGetDetailOutputApiModel;
  layoutData: LandingSiteTemplateGetDataOutputApiModel;
}

const LandingDoctorProfilePage: FC<LandingDoctorProfilePageProps> = ({
  data,
  layoutData,
}) => {
  const landingAppointmentModal = useLandingAppointmentModal();
  const { openShare } = useApp();
  const language = useAppSelector((state) => state.App.language);
  const t = useT();
  const [commentsCount, setCommentsCount] = useState(0);
  const [extraData, setExtraData] =
    useState<LandingDoctorGetDetailExtraOutputApiModel | null>(null);
  const { getLabel: getShiftWorkLabel } = useCenterOptionEnums({ parentKey: CenterOptions.Enum.ShiftWork.key });

  useEffect(() => {
    const controller = new AbortController();

    const loadExtraData = async () => {
      try {
        const r = await Api.LandingDoctor.getDetailExtra(
          { Id: data.Id },
          controller.signal,
        );
        if (r.Success && r.Result) setExtraData(r.Result);
      } catch {
        // ignore abort and transient request failures
      }
    };

    void loadExtraData();
    return () => controller.abort();
  }, [data.Id]);

  const centerBranch = useMemo(
    () => data.CenterBranches.find((p) => p.Id == data.CenterBranchId)!,
    [data.CenterBranches, data.CenterBranchId],
  );
  const briefDescription =
    resolveLocalizedText(
      data.BriefDescription,
      language,
      countryConfig.locale,
    ) ??
    resolveLocalizedText(
      centerBranch.BriefDescription,
      language,
      countryConfig.locale,
    );
  const bio =
    resolveLocalizedText(data.Bio, language, countryConfig.locale) ??
    resolveLocalizedText(centerBranch.Bio, language, countryConfig.locale);
  const appointmentBookingRules =
    resolveLocalizedText(
      data.AppointmentBookingRules,
      language,
      countryConfig.locale,
    ) ??
    resolveLocalizedText(
      centerBranch.AppointmentBookingRules,
      language,
      countryConfig.locale,
    );
  const appointmentCancellationRules =
    resolveLocalizedText(
      data.AppointmentCancellationRules,
      language,
      countryConfig.locale,
    ) ??
    resolveLocalizedText(
      centerBranch.AppointmentCancellationRules,
      language,
      countryConfig.locale,
    );
  const socialNetworks = useMemo(
    () =>
      _.isEmpty(extraData?.SocialNetworks)
        ? (extraData?.CenterSocialNetworks ?? [])
        : (extraData?.SocialNetworks ?? []),
    [extraData?.SocialNetworks, extraData?.CenterSocialNetworks],
  );
  const workProgramTab = useMemo(() => {
    // 1) join + filter by branch
    const items = (extraData?.CenterPersonnelWeekDayShiftWorks ?? [])
      .map((w) => {
        const Shift = (extraData?.CenterShiftWorks ?? []).find(
          (s) => s.Id === w.CenterShiftWorkId,
        );
        const WeekDay = (extraData?.WeekDays ?? []).find(
          (d) => d.Id === w.WeekDayId,
        );
        return { w, Shift, WeekDay };
      })
      .filter(
        (x) =>
          x.Shift &&
          x.WeekDay &&
          x.Shift.CenterBranchId === centerBranch.Id,
      );

    // 2) group by weekday (prevent duplicates)
    const grouped = _.groupBy(items, (x) => x.WeekDay!.Id);

    // 3) keep weekday order based on data.WeekDays (recommended)
    const orderedWeekDays = (extraData?.WeekDays ?? []).filter(
      (d) => grouped[d.Id],
    );

    return (
      <div className="doc-information-details">
        <div className="hours-business">
          <ul>
            {orderedWeekDays.map((weekDay) => {
              const rows = grouped[weekDay.Id] ?? [];

              return (
                <li key={weekDay.Id}>
                  <h6 className="m-0">{weekDay.Name}</h6>

                  {/* اگر یک روز چند شیفت داشت، همه رو نشون می‌دیم */}
                  <div className="d-flex flex-wrap gap-2">
                    {rows
                      .map((r) => r.Shift!)
                      .sort((a, b) =>
                        String(a.Start).localeCompare(String(b.Start)),
                      )
                      .map((shift) => (
                        <p key={shift.Id} style={{ margin: 0 }}>
                          <Format type="Time" value={shift.Start} /> -{" "}
                          <Format type="Time" value={shift.End} />
                        </p>
                      ))}
                  </div>
                </li>
              );
            })}

            {/* حالت خالی */}
            {orderedWeekDays.length === 0 && (
              <li>
                <p style={{ margin: 0 }}>
                  {t(
                    "landing.profile.message.noWorkProgramForBranch",
                    undefined,
                    "No work schedule has been registered for this branch.",
                  )}
                </p>
              </li>
            )}
          </ul>
        </div>
      </div>
    );
  }, [
    extraData?.CenterPersonnelWeekDayShiftWorks,
    extraData?.CenterShiftWorks,
    extraData?.WeekDays,
    centerBranch.Id,
    t,
  ]);
  const holidaysTab = useMemo(
    () => <Holidays dates={extraData?.Holidays ?? []} />,
    [extraData?.Holidays],
  );

  return (
    <>
      <LandingConversationTarget targetCenterId={data.CenterId} />
      <Breadcrumb
        title={data.Title}
        items={[centerBranch.DoctorNicknameSingular, data.FullName]}
        addTitleToEndItems={false}
      />

      {/* Page Content */}
      <div className="content">
        <div className="container">
          {/* Doctor Widget */}
          <div className="card doc-profile-card">
            <div className="card-body">
              <div className="doctor-widget doctor-profile-two">
                <div className="doc-info-left">
                  <div className="doctor-img text-center w-100">
                    <SmartImage
                      cover
                      src={data.Image?.Url}
                      className="img-fluid m-auto"
                      alt={data.Image?.AltText ?? data.FullName}
                      width={180}
                      height={180}
                      objectFit="contain"
                    />
                  </div>
                  <div
                    className="doc-info-cont"
                    style={{ marginInlineEnd: 10 }}
                  >
                    {/* <span className="badge doc-avail-badge">
                                                <i className="fa-solid fa-circle" />
                                                در دسترس{" "}
                                            </span> */}
                    <Space className="doc-name" wrap>
                      <Title level={3}>{data.FullName}</Title>
                      {/* <Image
                                                    src="/Templates/Doccure/img/icons/badge-check.svg"
                                                    alt="badge-check"
                                                    width={16}
                                                    height={16}
                                                /> */}

                      <If condition={data.BlueTick}>
                        <MdVerified color="#1DA1F2" size={18} />
                      </If>
                      {/* <span className="badge doctor-role-badge">
                                                    <i className="fa-solid fa-circle" />
                                                    Dentist
                                                </span> */}
                    </Space>
                    <If condition={briefDescription}>
                      <Paragraph style={{ maxWidth: 450 }}>
                        {briefDescription}
                      </Paragraph>
                    </If>
                    <If condition={centerBranch.Address}>
                      <p className="address-detail" style={{ maxWidth: 450 }}>
                        <span className="loc-icon">
                          <FiMapPin />
                        </span>
                        <Paragraph style={{ maxWidth: 450 }}>
                          {centerBranch.Address}
                        </Paragraph>
                        {centerBranch.MapLink && (
                          <SmartLink href={centerBranch.MapLink}>
                            <span className="view-text">
                              (
                              {t(
                                "landing.profile.action.viewOnMap",
                                undefined,
                                "View on map",
                              )}
                              )
                            </span>
                          </SmartLink>
                        )}
                      </p>
                    </If>
                    <If condition={!_.isEmpty(centerBranch.Tels)}>
                      <p className="address-detail" style={{ maxWidth: 450 }}>
                        <span className="loc-icon">
                          <FiPhone />
                        </span>
                        <Space wrap>
                          {centerBranch.Tels.map((tel) => (
                            <PhoneNumberTag key={tel} value={tel} />
                          ))}
                        </Space>
                      </p>
                    </If>
                  </div>
                </div>
                <div className="doc-info-right">
                  <ul className="doctors-activities">
                    <li>
                      <div className="hospital-info">
                        <span className="list-icon">
                          <SmartImage
                            src="/Templates/Doccure/img/icons/watch-icon.svg"
                            alt="Img"
                            width={18}
                            height={18}
                          />
                        </span>
                        <Text>
                          {t(
                            "landing.profile.status.fullTimeOnlineTreatmentAvailable",
                            undefined,
                            "Full-time, online treatment available",
                          )}
                        </Text>
                      </div>
                      <ul className="sub-links">
                        <li>
                          <FavoriteToggle
                            ids={{
                              type: CenterOptions.Enum.UserFavoriteType.Doctor
                                .key,
                              centerUserDoctorId: data.Id,
                            }}
                            defaultFavorite={extraData?.Favorite ?? false}
                          />
                        </li>
                        <li>
                          <a
                            onClick={() => {
                              openShare({
                                link: `${Constants.SiteAddress}${data.ShortLink}`,
                                text: t(
                                  "landing.profile.share.doctor",
                                  {
                                    nickname:
                                      centerBranch.DoctorNicknameSingular,
                                    name: data.FullName,
                                  },
                                  "View {nickname} {name} on Clinic24",
                                ),
                              });
                            }}
                          >
                            <FiShare2 />
                          </a>
                        </li>
                        {/* <li>
                                                        <SmartLink href="#">
                                                            <FiHeart />
                                                        </SmartLink>
                                                    </li> */}
                      </ul>
                    </li>
                    <li>
                      <div className="hospital-info">
                        <span className="list-icon">
                          <SmartImage
                            src="/Templates/Doccure/img/icons/thumb-icon.svg"
                            alt="Img"
                            width={18}
                            height={18}
                          />
                        </span>
                        <Text>
                          <b>
                            {(
                              (data.CommentsAvg > 0 ? data.CommentsAvg : 5) * 20
                            ).toFixed(1)}
                            %{" "}
                          </b>{" "}
                          {t(
                            "landing.profile.status.recommended",
                            undefined,
                            "recommended",
                          )}
                        </Text>
                      </div>
                    </li>
                    {data.CenterBranches.map((centerBranch) => (
                      <li key={centerBranch.Id}>
                        <SmartLink href={centerBranch.Link}>
                          <div className="hospital-info">
                            <span className="list-icon">
                              <SmartImage
                                cover
                                src={centerBranch.Image?.Url}
                                alt={
                                  centerBranch.Image?.AltText ??
                                  centerBranch.Name
                                }
                                width={18}
                                height={18}
                              />
                            </span>
                            <Text>{centerBranch.Name}</Text>
                          </div>
                        </SmartLink>
                        {/* <h5 className="accept-text">
                                                    <span>
                                                        <FiCheck />
                                                    </span>
                                                    پذیرای بیماران جدید
                                                </h5> */}
                      </li>
                    ))}

                    <li>
                      <div className="rating">
                        <RatingStars value={data.CommentsAvg} />
                        <span>{data.CommentsAvg.toFixed(1)}</span>
                        <span className="d-inline-block average-rating">
                          {t(
                            "landing.profile.label.commentsCount",
                            { count: data.CommentsCount },
                            "{count} reviews",
                          )}
                        </span>
                        <span className="d-inline-block average-rating">
                          {" | "}
                          {t(
                            "common.field.visitorCount",
                            undefined,
                            "Visit count",
                          )}
                          :{" "}
                          <Format type="Number" value={data.VisitStatistics} />
                        </span>
                      </div>
                      <If condition={!_.isEmpty(socialNetworks)}>
                        <ul className="sub-links">
                          {socialNetworks.map((socialNetwork) => (
                            <li key={socialNetwork.Id}>
                              <SmartLink
                                href={socialNetwork.Link}
                                title={socialNetwork.Name}
                              >
                                <Icon icon={socialNetwork.Icon} />
                              </SmartLink>
                            </li>
                          ))}
                        </ul>
                      </If>
                      {/* <ul className="contact-doctors">
                                                    <li>
                                                        <SmartLink href="#">
                                                            <span>
                                                                <Image
                                                                    src="/Templates/Doccure/img/icons/device-message2.svg"
                                                                    alt="Img"
                                                                    width={14}
                                                                    height={13}
                                                                />
                                                            </span>
                                                            چت
                                                        </SmartLink>
                                                    </li>
                                                    <li>
                                                        <SmartLink href="#">
                                                            <span className="bg-violet">
                                                                <FiPhoneForwarded />
                                                            </span>
                                                            تماس صوتی
                                                        </SmartLink>
                                                    </li>
                                                    <li>
                                                        <SmartLink href="#">
                                                            <span className="text-indigo">
                                                                <FaVideo />
                                                            </span>
                                                            تماس تصویری
                                                        </SmartLink>
                                                    </li>
                                                </ul> */}
                    </li>
                  </ul>
                </div>
              </div>
              <div className="doc-profile-card-bottom">
                <ul>
                  {/* <li>
                                            <span className="bg-blue">
                                                <Image
                                                    src="/Templates/Doccure/img/icons/calendar3.svg"
                                                    alt="Img"
                                                    width={16}
                                                    height={16}
                                                />
                                            </span>
                                            تقریباً بیش از ۲۰۰ نوبت دریافت شده
                                        </li>
                                        <li>
                                            <span className="bg-dark-blue">
                                                <Image
                                                    src="/Templates/Doccure/img/icons/bullseye.svg"
                                                    alt="Img"
                                                    width={16}
                                                    height={16}
                                                />
                                            </span>
                                            21 سال سابقه
                                        </li>
                                        <li>
                                            <span className="bg-green">
                                                <Image
                                                    src="/Templates/Doccure/img/icons/bookmark-star.svg"
                                                    alt="Img"
                                                    width={12}
                                                    height={16}
                                                />
                                            </span>
                                            15+ جایزه
                                        </li> */}

                  <li>
                    <Flex align="center" justify="center">
                      <span className="bg-dark-blue">
                        <SmartImage
                          src="/Templates/Doccure/img/icons/bullseye.svg"
                          alt="Img"
                          width={16}
                          height={16}
                        />
                      </span>

                      <Paragraph className="m-0">
                        <Html value={data.Partnership} />
                      </Paragraph>
                    </Flex>
                  </li>
                </ul>
                <div className="bottom-book-btn">
                  <div className="clinic-booking">
                    <If condition={data.HasPrimaryModule}>
                      <a
                        className="apt-btn"
                        onClick={async () => {
                          await landingAppointmentModal.showBooking({
                            values: {
                              CenterId: data.CenterId,
                              CenterBranchId: data.CenterBranchId,
                              CenterUserDoctorId: data.CenterUserId,
                            },
                          });
                        }}
                      >
                        {t(
                          "landing.profile.action.bookAppointment",
                          undefined,
                          "Book appointment",
                        )}
                      </a>
                    </If>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {data.HasPrimaryModule ? (
            <Card
              tabProps={{
                centered: true,
              }}
              tabList={[
                {
                  forceRender: true,
                  key: "Info",
                  label: t("landing.profile.tab.info", undefined, "Profile"),
                  children: (
                    <div>
                      <If condition={bio}>
                        <div className="doc-information-details bio-detail">
                          <div className="detail-title">
                            <h4>
                              {t(
                                "landing.profile.field.biography",
                                undefined,
                                "Biography",
                              )}
                            </h4>
                          </div>
                          <Html value={bio} />
                        </div>
                      </If>

                      <If condition={appointmentBookingRules}>
                        <div className="doc-information-details bio-detail">
                          <div className="detail-title">
                            <h4>
                              {t(
                                "commonAccount.profile.field.bookingRules",
                                undefined,
                                "Booking rules",
                              )}
                            </h4>
                          </div>
                          <Html value={appointmentBookingRules} />
                        </div>
                      </If>

                      <If condition={appointmentCancellationRules}>
                        <div className="doc-information-details bio-detail">
                          <div className="detail-title">
                            <h4>
                              {t(
                                "commonAccount.profile.field.cancellationRules",
                                undefined,
                                "Cancellation rules",
                              )}
                            </h4>
                          </div>
                          <Html value={appointmentCancellationRules} />
                        </div>
                      </If>

                      <div className="doc-information-details" id="clinic">
                        <div className="detail-title">
                          <h4>
                            {t("common.field.branches", undefined, "Branches")}
                          </h4>
                        </div>
                        {data.CenterBranches.map((centerBranch) => (
                          <div key={centerBranch.Id} className="clinic-loc">
                            <div className="row align-items-center">
                              <div className="col-lg-7">
                                <div className="clinic-info">
                                  <div className="clinic-img">
                                    <SmartImage
                                      cover
                                      src={centerBranch.Image?.Url}
                                      alt={
                                        centerBranch.Image?.AltText ??
                                        centerBranch.Name
                                      }
                                      width={80}
                                      height={80}
                                    />
                                  </div>
                                  <div className="detail-clinic">
                                    <h5>{centerBranch.Name}</h5>
                                    {/* <span>$350 / Apponitment</span> */}
                                    <If condition={centerBranch.Address}>
                                      <p
                                        className="address-detail"
                                        style={{ maxWidth: 450 }}
                                      >
                                        <span className="loc-icon">
                                          <FiMapPin />
                                        </span>
                                        <Paragraph style={{ maxWidth: 450 }}>
                                          {centerBranch.Address}
                                        </Paragraph>
                                        {centerBranch.MapLink && (
                                          <SmartLink
                                            href={centerBranch.MapLink}
                                          >
                                            <span className="view-text">
                                              (
                                              {t(
                                                "landing.profile.action.viewOnMap",
                                                undefined,
                                                "View on map",
                                              )}
                                              )
                                            </span>
                                          </SmartLink>
                                        )}
                                      </p>
                                    </If>
                                    <If
                                      condition={!_.isEmpty(centerBranch.Tels)}
                                    >
                                      <p
                                        className="address-detail"
                                        style={{ maxWidth: 450 }}
                                      >
                                        <span className="loc-icon">
                                          <FiPhone />
                                        </span>
                                        <Space wrap>
                                          {centerBranch.Tels.map((tel) => (
                                            <PhoneNumberTag
                                              key={tel}
                                              value={tel}
                                            />
                                          ))}
                                        </Space>
                                      </p>
                                    </If>
                                  </div>
                                </div>
                                <Flex wrap gap={8} className="avail-time-slot">
                                  {(
                                    extraData?.CenterPersonnelWeekDayShiftWorks ??
                                    []
                                  )
                                    .map((item) => {
                                      const Shift = (
                                        extraData?.CenterShiftWorks ?? []
                                      ).find(
                                        (p) => p.Id == item.CenterShiftWorkId,
                                      );
                                      const WeekDay = (
                                        extraData?.WeekDays ?? []
                                      ).find((p) => p.Id == item.WeekDayId);
                                      return {
                                        key: `${item.CenterShiftWorkId}.${item.WeekDayId}`,
                                        Shift,
                                        WeekDay,
                                      };
                                    })
                                    .filter(
                                      (item) =>
                                        item.Shift &&
                                        item.WeekDay &&
                                        item.Shift.CenterBranchId ==
                                        centerBranch.Id,
                                    )
                                    .map(({ key, Shift, WeekDay }) => {
                                      return (
                                        <div
                                          key={key}
                                          className="availability-date"
                                        >
                                          <div className="book-date m-0">
                                            <h6>
                                              {WeekDay!.Name} (
                                              {getShiftWorkLabel(Shift?.ShiftKey)}
                                              )
                                            </h6>
                                            <span>
                                              <Format
                                                type="Time"
                                                value={Shift!.Start}
                                              />{" "}
                                              -{" "}
                                              <Format
                                                type="Time"
                                                value={Shift!.End}
                                              />
                                            </span>
                                          </div>
                                        </div>
                                      );
                                    })}
                                </Flex>
                              </div>
                              <If condition={centerBranch.Location}>
                                <div className="col-lg-5">
                                  <div className="contact-map d-flex">
                                    <iframe
                                      src={centerBranch.Location}
                                      allowFullScreen
                                      loading="lazy"
                                      referrerPolicy="no-referrer-when-downgrade"
                                    />
                                  </div>
                                </div>
                              </If>

                              <If condition={centerBranch.Bio}>
                                <div className="col-12 mt-3">
                                  <div className="doc-information-details bio-detail">
                                    <div className="detail-title">
                                      <h4>
                                        {t(
                                          "landing.profile.field.biography",
                                          undefined,
                                          "Biography",
                                        )}
                                      </h4>
                                    </div>
                                    <Html value={centerBranch.Bio} />
                                  </div>
                                </div>
                              </If>

                              <If
                                condition={centerBranch.AppointmentBookingRules}
                              >
                                <div className="col-12 mt-3">
                                  <div className="doc-information-details bio-detail">
                                    <div className="detail-title">
                                      <h4>
                                        {t(
                                          "commonAccount.profile.field.bookingRules",
                                          undefined,
                                          "Booking rules",
                                        )}
                                      </h4>
                                    </div>
                                    <Html
                                      value={
                                        centerBranch.AppointmentBookingRules
                                      }
                                    />
                                  </div>
                                </div>
                              </If>

                              <If
                                condition={
                                  centerBranch.AppointmentCancellationRules
                                }
                              >
                                <div className="col-12 mt-3">
                                  <div className="doc-information-details bio-detail">
                                    <div className="detail-title">
                                      <h4>
                                        {t(
                                          "commonAccount.profile.field.cancellationRules",
                                          undefined,
                                          "Cancellation rules",
                                        )}
                                      </h4>
                                    </div>
                                    <Html
                                      value={
                                        centerBranch.AppointmentCancellationRules
                                      }
                                    />
                                  </div>
                                </div>
                              </If>
                            </div>
                          </div>
                        ))}
                      </div>

                      <If condition={!_.isEmpty(extraData?.CenterFaqs)}>
                        <Faq
                          pageData={layoutData}
                          dataSource={{ Items: extraData?.CenterFaqs ?? [] }}
                          setting={defaultFaqSetting}
                        />
                      </If>
                    </div>
                  ),
                },
                {
                  forceRender: true,
                  key: "Services",
                  label:
                    (extraData?.Services?.length ?? 0)
                      ? `${t("common.field.services", undefined, "Services")} (${extraData?.Services?.length})`
                      : t("common.field.services", undefined, "Services"),
                  children: (
                    <Commodities
                      pageData={layoutData}
                      dataSource={{
                        Departments: extraData?.Departments ?? [],
                        Items: extraData?.Services ?? [],
                      }}
                      setting={{
                        Title: t(
                          "common.field.services",
                          undefined,
                          "Services",
                        ),
                        Description: "",
                        Type: Helper.centerOptionObjectToCenterOptionModel(CenterOptions.Enum.CenterDepartmentType.Service),
                        ItemsDisplayMode:
                          Helper.centerOptionObjectToCenterOptionModel(
                            CenterOptions.SiteTemplate.SiteTemplateSettings
                              .ItemsDisplayMode.Grid,
                          ),
                        DisplaySubordinateCenterData:
                          Helper.centerOptionObjectToCenterOptionModel(CenterOptions.Enum.Status.InActive),
                        ShowServicesCommoditiesWithCategory:
                          Helper.centerOptionObjectToCenterOptionModel(CenterOptions.Enum.Status.InActive),
                      }}
                      showViewAllButton={false}
                      showBackgroundImage={false}
                      syncDepartmentStatsWithItems
                    />
                  ),
                },
                {
                  forceRender: true,
                  key: "Commodities",
                  label:
                    (extraData?.Commodities?.length ?? 0)
                      ? `${t("common.field.commodities", undefined, "Commodities")} (${extraData?.Commodities?.length})`
                      : t("common.field.commodities", undefined, "Commodities"),
                  children: (
                    <Commodities
                      pageData={layoutData}
                      dataSource={{
                        Departments: extraData?.Departments ?? [],
                        Items: extraData?.Commodities ?? [],
                      }}
                      setting={{
                        Title: t(
                          "common.field.commodities",
                          undefined,
                          "Commodities",
                        ),
                        Description: "",
                        Type: Helper.centerOptionObjectToCenterOptionModel(CenterOptions.Enum.CenterDepartmentType.Commodity),
                        ItemsDisplayMode:
                          Helper.centerOptionObjectToCenterOptionModel(
                            CenterOptions.SiteTemplate.SiteTemplateSettings
                              .ItemsDisplayMode.Grid,
                          ),
                        DisplaySubordinateCenterData:
                          Helper.centerOptionObjectToCenterOptionModel(CenterOptions.Enum.Status.InActive),
                        ShowServicesCommoditiesWithCategory:
                          Helper.centerOptionObjectToCenterOptionModel(CenterOptions.Enum.Status.InActive),
                      }}
                      showViewAllButton={false}
                      showBackgroundImage={false}
                      syncDepartmentStatsWithItems
                    />
                  ),
                },
                {
                  forceRender: true,
                  key: "Holidays",
                  label: t(
                    "landing.profile.tab.holidays",
                    undefined,
                    "Holidays",
                  ),
                  children: holidaysTab,
                },
                {
                  forceRender: true,
                  key: "WorkProgram",
                  label: t(
                    "landing.profile.tab.workProgram",
                    undefined,
                    "Work schedule",
                  ),
                  children: workProgramTab,
                },
                {
                  forceRender: true,
                  key: "Comments",
                  label: commentsCount
                    ? `${t("landing.profile.tab.comments", undefined, "Reviews")} (${commentsCount})`
                    : t("landing.profile.tab.comments", undefined, "Reviews"),
                  children: (
                    <LandingCommentListPage
                      onGetList={(list) => setCommentsCount(list.length)}
                      TypeKey={CenterOptions.Enum.CommentType.Doctor.key}
                      CenterUserDoctorId={data.CenterUserId}
                    />
                  ),
                },
                {
                  forceRender: true,
                  key: "Blogs",
                  label:
                    (extraData?.Blogs?.length ?? 0)
                      ? `${t("landing.profile.tab.blogs", undefined, "Blogs")} (${extraData?.Blogs?.length})`
                      : t("landing.profile.tab.blogs", undefined, "Blogs"),
                  children: (
                    <Blogs
                      pageData={layoutData}
                      dataSource={{ Items: extraData?.Blogs ?? [] }}
                      setting={defaultBlogsSetting}
                    // showViewAllButton={false}
                    />
                  ),
                },
                {
                  forceRender: true,
                  key: "Gallery",
                  label:
                    (extraData?.GalleryItems?.length ?? 0)
                      ? `${t("center.gallery.entity", undefined, "Gallery")} (${extraData?.GalleryItems?.length})`
                      : t("center.gallery.entity", undefined, "Gallery"),
                  children: (
                    <Gallery
                      pageData={layoutData}
                      dataSource={{
                        Categories: extraData?.GalleryCategories ?? [],
                        Items: extraData?.GalleryItems ?? [],
                      }}
                      setting={{
                        Title: "",
                        Description: "",
                        ItemsDisplayMode:
                          Helper.centerOptionObjectToCenterOptionModel(
                            CenterOptions.SiteTemplate.SiteTemplateSettings
                              .ItemsDisplayMode.Grid,
                          ),
                      }}
                      showBackgroundImage={false}
                    />
                  ),
                },
              ]}
            />
          ) : (
            <Alert
              type="warning"
              title={t(
                "landing.profile.message.inactiveSubscriptionWarning",
                undefined,
                "This center is registered in the system but does not have an active subscription. Please activate the subscription to access the profile.",
              )}
              className="text-center"
            />
          )}
        </div>
      </div>
      {/* /Page Content */}
    </>
  );
};

export default LandingDoctorProfilePage;
